import * as React from "react";
import { useForm, SubmitHandler, FormProvider } from "react-hook-form";
import { processValues } from "./utils/processValues";
import { newSearch } from "../../services/newSearch";
import { useSearchStore } from "./hooks/useSearchStore";
import { useMonth } from "../../hooks/useMonth";
import { monthSlugs } from "../../services/centralStore";
import { createContext, useState } from "react";
import { navigate } from "gatsby";

type SearchFormContextType = {
    loading: boolean;
    results: any[];
    categories: { [key: string]: number };
    apiCall: any;
    onFormSubmit: SubmitHandler<SearchFormInputs>;
};

export const SearchFormContext = createContext<SearchFormContextType>({
    loading: false,
    results: [],
    categories: {},
    apiCall: {},
    onFormSubmit: () => {}
});

type SearchFormCoreProps = {
    valuesFromStore?: boolean;
    defaultValues?: Partial<SearchFormInputs>;
    submitHandler?: () => void;
};

export const SearchFormCore = ({
    valuesFromStore,
    defaultValues,
    submitHandler,
    children
}: React.PropsWithChildren<SearchFormCoreProps>) => {
    const { month } = useMonth({ ignoreOverview: true });
    const monthIndex = monthSlugs.indexOf(month) + 1;
    const formValues = useSearchStore((state) => state.formValues);
    const methods = useForm<SearchFormInputs>({
        defaultValues:
            defaultValues || (valuesFromStore && formValues
                ? formValues
                : {
                      focus: "beach",
                      month: (monthIndex > 12 ? 1 : monthIndex) as SearchMonth,
                      prices: {
                          flightOrigin: "easternUs"
                      },
                      other: {
                          visaOrigin: "usa"
                      }
                  })
    });
    const setFormValues = useSearchStore((state) => state.setFormValues);

    const setFormTouched = useSearchStore((state) => state.setFormTouched);
    const [categories, setCategories] = useState({});
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [apiCall, setApiCall] = useState({});
    const onFormSubmit: SubmitHandler<SearchFormInputs> = (data) => {
        setFormValues(data);
        navigate("/search-results");
        submitHandler && submitHandler();
    };
    const sumbitFc = async () => {
        const values = methods.getValues();
        const { apiCall, searchCategories } = processValues(values);
        setCategories(searchCategories);
        setApiCall(apiCall);
        setLoading(true);
        const results = await newSearch(apiCall);
        setLoading(false);
        setResults(results);
    };
    React.useEffect(() => {
        sumbitFc();
        return methods.watch(async (_, evt) => {
            window &&
                window.dataLayer.push({
                    event: "search_interaction",
                    fieldName: evt.name
                });
            setFormTouched(true);
            sumbitFc();
        }).unsubscribe;
    }, []);

    return (
        <SearchFormContext.Provider
            value={{ loading, results, categories, apiCall, onFormSubmit }}
        >
            <FormProvider {...methods}>
                <form className="w-full p-4">{children}</form>
            </FormProvider>
        </SearchFormContext.Provider>
    );
};
